import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('izzishow_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('izzishow_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item) => {
    // item structure: { id, type: 'location'|'equipment'|'talent', name, price, image, data: {...} }
    setCart(prev => {
      // Check if item already exists
      const existingIndex = prev.findIndex(
        cartItem => cartItem.id === item.id && cartItem.type === item.type
      );

      if (existingIndex > -1) {
        // Update existing item
        const newCart = [...prev];
        newCart[existingIndex] = { ...newCart[existingIndex], ...item };
        return newCart;
      } else {
        // Add new item with default dates
        return [...prev, { ...item, dates: { start: '', end: '' } }];
      }
    });
  };

  const removeFromCart = (id, type) => {
    setCart(prev => prev.filter(item => !(item.id === id && item.type === type)));
  };

  const updateCartItemDates = (id, type, dates) => {
    setCart(prev => prev.map(item => 
      (item.id === id && item.type === type) 
        ? { ...item, dates }
        : item
    ));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('izzishow_cart');
  };

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return days > 0 ? days : 0;
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      if (item.dates && item.dates.start && item.dates.end) {
        const days = calculateDays(item.dates.start, item.dates.end);
        return total + (item.price * days);
      }
      return total;
    }, 0);
  };

  const getCartItemsCount = () => {
    return cart.length;
  };

  const isInCart = (id, type) => {
    return cart.some(item => item.id === id && item.type === type);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateCartItemDates,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    isInCart,
    calculateDays,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};